import { NotificationConfig, NotificationType } from '@routific/hive';
import i18n from '../i18n';

const CREATE_ORDERS_FROM_CUSTOMERS_SUCCESS_KEY = 'create-orders-from-customers-success';
const CREATE_ORDERS_FROM_CUSTOMERS_SKIPPED_KEY = 'create-orders-from-customers-skipped';
const CREATE_ORDERS_FROM_CUSTOMERS_NO_RESULT_KEY = 'create-orders-from-customers-no-result';
const CREATE_ORDERS_FROM_CUSTOMERS_ERROR_KEY = 'create-orders-from-customers-error';
const CREATE_ORDERS_FROM_CUSTOMERS_NO_PROJECT_KEY = 'create-orders-from-customers-no-project';

export const createOrdersFromCustomersSuccessNotification = (
  count: number,
  onButtonClick?: () => void,
  targetPage: 'orders' | 'routes' = 'routes',
): NotificationConfig => ({
  id: CREATE_ORDERS_FROM_CUSTOMERS_SUCCESS_KEY,
  type: NotificationType.SUCCESS,
  message: String(i18n.t('notifications:createOrdersFromCustomers.success', { count })),
  linkMessage: onButtonClick
    ? i18n.t(`notifications:createOrdersFromCustomers.${targetPage === 'orders' ? 'success_orders_link' : 'success_link'}`, { count })
    : undefined,
  linkOnClick: onButtonClick,
  priority: true,
  dataCy: CREATE_ORDERS_FROM_CUSTOMERS_SUCCESS_KEY,
});

export const createOrdersFromCustomersSkippedNotification = (
  count: number,
  reasons?: string,
): NotificationConfig => ({
  id: CREATE_ORDERS_FROM_CUSTOMERS_SKIPPED_KEY,
  type: NotificationType.WARNING,
  message: String(i18n.t('notifications:createOrdersFromCustomers.skipped', {
    count,
    reasons: reasons ? `: ${reasons}` : '',
  })),
  priority: true,
  dataCy: CREATE_ORDERS_FROM_CUSTOMERS_SKIPPED_KEY,
});

export const createOrdersFromCustomersNoResultNotification = (count: number): NotificationConfig => ({
  id: CREATE_ORDERS_FROM_CUSTOMERS_NO_RESULT_KEY,
  type: NotificationType.ERROR,
  message: String(i18n.t('notifications:createOrdersFromCustomers.no_result', { count })),
  priority: true,
});

export const createOrdersFromCustomersErrorNotification = (count: number): NotificationConfig => ({
  id: CREATE_ORDERS_FROM_CUSTOMERS_ERROR_KEY,
  type: NotificationType.ERROR,
  message: String(i18n.t('notifications:createOrdersFromCustomers.error', { count })),
  priority: true,
  dataCy: CREATE_ORDERS_FROM_CUSTOMERS_ERROR_KEY,
});

export const createOrdersFromCustomersNoProjectNotification = (): NotificationConfig => ({
  id: CREATE_ORDERS_FROM_CUSTOMERS_NO_PROJECT_KEY,
  type: NotificationType.ERROR,
  message: String(i18n.t('notifications:createOrdersFromCustomers.no_project')),
  priority: true,
});

const CREATE_CUSTOMER_PROFILE_SUCCESS_KEY = 'create-customer-profile-success';
const CREATE_CUSTOMER_PROFILE_ERROR_KEY = 'create-customer-profile-error';
const UPDATE_CUSTOMER_PROFILE_SUCCESS_KEY = 'update-customer-profile-success';
const UPDATE_CUSTOMER_PROFILE_ERROR_KEY = 'update-customer-profile-error';
const DELETE_CUSTOMER_PROFILE_SUCCESS_KEY = 'delete-customer-profile-success';
const DELETE_CUSTOMER_PROFILE_ERROR_KEY = 'delete-customer-profile-error';

export const createCustomerProfileSuccessNotification = (): NotificationConfig => ({
  id: CREATE_CUSTOMER_PROFILE_SUCCESS_KEY,
  type: NotificationType.SUCCESS,
  message: String(i18n.t('notifications:createCustomerProfile.success')),
  priority: true,
  dataCy: CREATE_CUSTOMER_PROFILE_SUCCESS_KEY,
});

export const createCustomerProfileErrorNotification = (): NotificationConfig => ({
  id: CREATE_CUSTOMER_PROFILE_ERROR_KEY,
  type: NotificationType.ERROR,
  message: String(i18n.t('notifications:createCustomerProfile.error')),
  priority: true,
  dataCy: CREATE_CUSTOMER_PROFILE_ERROR_KEY,
});

const CREATE_CUSTOMERS_FROM_ORDERS_SUCCESS_KEY = 'create-customers-from-orders-success';
const CREATE_CUSTOMERS_FROM_ORDERS_SKIPPED_KEY = 'create-customers-from-orders-skipped';
const CREATE_CUSTOMERS_FROM_ORDERS_ERROR_KEY = 'create-customers-from-orders-error';
const CREATE_CUSTOMERS_FROM_ORDERS_NO_PROJECT_KEY = 'create-customers-from-orders-no-project';

export const createCustomersFromOrdersSuccessNotification = (
  count: number,
  onButtonClick?: () => void,
): NotificationConfig => ({
  id: CREATE_CUSTOMERS_FROM_ORDERS_SUCCESS_KEY,
  type: NotificationType.SUCCESS,
  message: i18n.t('notifications:createCustomersFromOrders.success', { count }),
  linkMessage: onButtonClick
    ? i18n.t('notifications:createCustomersFromOrders.success_link', { count })
    : undefined,
  linkOnClick: onButtonClick,
  priority: true,
  dataCy: CREATE_CUSTOMERS_FROM_ORDERS_SUCCESS_KEY,
});

export const createCustomersFromOrdersSkippedNotification = (
  count: number,
): NotificationConfig => ({
  id: CREATE_CUSTOMERS_FROM_ORDERS_SKIPPED_KEY,
  type: NotificationType.WARNING,
  message: i18n.t('notifications:createCustomersFromOrders.skipped', { count }),
  priority: true,
  dataCy: CREATE_CUSTOMERS_FROM_ORDERS_SKIPPED_KEY,
});

export const createCustomersFromOrdersErrorNotification = (
  count: number,
  skippedReasons?: string[],
): NotificationConfig => {
  const reasonMessage = skippedReasons?.length
    ? [...new Set(skippedReasons)]
      .map((reason) => String(i18n.t(`notifications:createCustomersFromOrders.skip_reason.${reason}`)))
      .join(' ')
    : undefined;

  return {
    id: CREATE_CUSTOMERS_FROM_ORDERS_ERROR_KEY,
    type: NotificationType.ERROR,
    message: reasonMessage || i18n.t('notifications:createCustomersFromOrders.error', { count }),
    priority: true,
    dataCy: CREATE_CUSTOMERS_FROM_ORDERS_ERROR_KEY,
  };
};

export const createCustomersFromOrdersNoProjectNotification = (): NotificationConfig => ({
  id: CREATE_CUSTOMERS_FROM_ORDERS_NO_PROJECT_KEY,
  type: NotificationType.ERROR,
  message: i18n.t('notifications:createCustomersFromOrders.no_project'),
  priority: true,
});
export const updateCustomerProfileSuccessNotification = (): NotificationConfig => ({
  id: UPDATE_CUSTOMER_PROFILE_SUCCESS_KEY,
  type: NotificationType.SUCCESS,
  message: i18n.t('notifications:updateCustomerProfile.success'),
  priority: true,
  dataCy: UPDATE_CUSTOMER_PROFILE_SUCCESS_KEY,
});

export const updateCustomerProfileErrorNotification = (): NotificationConfig => ({
  id: UPDATE_CUSTOMER_PROFILE_ERROR_KEY,
  type: NotificationType.ERROR,
  message: i18n.t('notifications:updateCustomerProfile.error'),
  priority: true,
  dataCy: UPDATE_CUSTOMER_PROFILE_ERROR_KEY,
});

export const deleteCustomerProfileSuccessNotification = (): NotificationConfig => ({
  id: DELETE_CUSTOMER_PROFILE_SUCCESS_KEY,
  type: NotificationType.SUCCESS,
  message: i18n.t('notifications:deleteCustomerProfile.success'),
  priority: true,
  dataCy: DELETE_CUSTOMER_PROFILE_SUCCESS_KEY,
});

export const deleteCustomerProfileErrorNotification = (): NotificationConfig => ({
  id: DELETE_CUSTOMER_PROFILE_ERROR_KEY,
  type: NotificationType.ERROR,
  message: i18n.t('notifications:deleteCustomerProfile.error'),
  priority: true,
  dataCy: DELETE_CUSTOMER_PROFILE_ERROR_KEY,
});
